import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LazyImage from '../common/LazyImage';
import { SparklesIcon, TagIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';

const ProductCard = ({ product, compact = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('shop');
  const { theme, getGradient } = useTheme();

  const formatPrice = (cents, currency) => {
    if (cents === 0) return t('promotion.free', 'FREE');
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(cents / 100);
  };

  const getProductTypeLabel = (type) => {
    const labels = {
      'pdf_download': t('productTypes.pdf'),
      'pdf_document': t('productTypes.pdfDocumentShort', 'PDF'),
      'pdf_guide': t('productTypes.pdfGuide', 'Guide'),
      'training_session': t('productTypes.session'),
      'training_program': t('productTypes.program'),
      'meal_plan': t('productTypes.mealPlan'),
      'meal_plan_structured': t('productTypes.mealPlanStructured', 'Meal Plan'),
      'nutrition_plan': t('productTypes.nutritionPlan', 'Meal Plan'),
      'excel_document': t('productTypes.excel', 'Excel'),
      'word_document': t('productTypes.word', 'Word'),
      'challenge': t('productTypes.challenge', 'Challenge'),
      'digital_download': t('productTypes.digitalDownload', 'Download')
    };
    return labels[type] || t('productTypes.generic', 'Product');
  };

  // Get effective pricing info
  const effectivePricing = product.effectivePricing || null;
  const isOnPromotion = effectivePricing?.isOnPromotion || false;
  const currentPrice = effectivePricing?.currentPriceCents ?? product.priceCents;
  const originalPrice = effectivePricing?.originalPriceCents ?? product.priceCents;
  const isFree = currentPrice === 0;

  // Calculate days remaining in promotion
  const getDaysRemaining = () => {
    if (!effectivePricing?.promotionEndsAt) return null;
    const end = new Date(effectivePricing.promotionEndsAt);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining();

  const handleClick = () => {
    navigate(`/shop/product/${product.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border-2 ${
        isOnPromotion ? 'border-orange-300 hover:border-orange-400' : `border-gray-100 ${theme.cardHoverBorder}`
      } ${
        compact 
          ? 'transform hover:-translate-y-1' 
          : 'transform hover:-translate-y-2'
      }`}
    >
      {/* Product Image Container */}
      <div className={`relative w-full overflow-hidden ${compact ? 'h-40' : 'h-56 md:h-64'}`}>
        {(product.thumbnailUrl || product.imageUrl) ? (
          <LazyImage
            src={product.thumbnailUrl || product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${getGradient(0)} flex items-center justify-center`}>
            <SparklesIcon className={`text-white/40 ${compact ? 'h-12 w-12' : 'h-20 w-20'}`} />
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* Promotion Badge */}
        {isOnPromotion && (
          <div className={`absolute bg-gradient-to-r ${getGradient(3)} backdrop-blur-sm rounded-full font-bold text-white shadow-lg animate-pulse ${
            compact ? 'top-2 left-2 px-2 py-1 text-[0.65rem]' : 'top-3 left-3 px-3 py-1.5 text-xs'
          }`}>
            <TagIcon className={`inline ${compact ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            {isFree ? t('promotion.freeLabel', 'FREE!') : t('promotion.sale', 'SALE!')}
          </div>
        )}

        {/* Brand Badge — portrait crest, shown via object-contain so it's never cropped */}
        <div className={`absolute ${
          compact ? (isOnPromotion ? 'top-9 left-2 h-11 w-9' : 'top-2 left-2 h-11 w-9') : isOnPromotion ? 'top-10 left-3 h-16 w-12' : 'top-3 left-3 h-16 w-12'
        }`}>
          <img
            src={process.env.PUBLIC_URL + '/badge.png?v=2'}
            alt=""
            className="w-full h-full object-contain drop-shadow-md"
          />
        </div>

        {/* Access Level Badge */}
        {product.accessLevel === 'trainer_members' && !isOnPromotion && (
          <div className={`absolute bg-gradient-to-r from-orange-500 to-red-500 backdrop-blur-sm rounded-full font-semibold text-white shadow-lg whitespace-nowrap max-w-[58%] truncate ${
            compact ? 'top-2 right-2 px-2 py-0.5 text-[0.55rem]' : 'top-3 right-3 px-2.5 py-0.5 text-[0.6rem]'
          }`}>
            {t('product.membersOnlyBadge')}
          </div>
        )}

        {/* Days Remaining Badge */}
        {isOnPromotion && daysRemaining !== null && daysRemaining <= 7 && (
          <div className={`absolute bg-black/70 backdrop-blur-sm rounded-full font-semibold text-white shadow-lg ${
            compact ? 'bottom-14 right-2 px-2 py-1 text-[0.6rem]' : 'bottom-20 right-3 px-3 py-1 text-xs'
          }`}>
            <ClockIcon className={`inline ${compact ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
            {daysRemaining === 0
              ? t('promotion.endsToday', 'Ends Today!')
              : t('promotion.endsInDays', { count: daysRemaining })}
          </div>
        )}

        {/* Product Info Overlay */}
        <div className={`absolute bottom-0 left-0 right-0 text-white ${compact ? 'p-3' : 'p-4'}`}>
          <h3 className={`font-bold line-clamp-2 mb-1.5 leading-snug ${compact ? 'text-xs' : 'text-sm md:text-base'}`}>
            {product.name}
          </h3>
          
          <div className="flex items-center justify-between gap-2">
            {/* Price — always fully visible (never truncated); the type tag yields space instead. */}
            <div className="shrink-0">
              {isOnPromotion ? (
                <div className="flex items-baseline gap-1.5">
                  {isFree ? (
                    <span className={`font-bold text-green-400 whitespace-nowrap ${compact ? 'text-base' : 'text-xl md:text-2xl'}`}>
                      {t('promotion.free', 'FREE')}
                    </span>
                  ) : (
                    <span className={`font-bold text-orange-300 whitespace-nowrap ${compact ? 'text-base' : 'text-xl md:text-2xl'}`}>
                      {formatPrice(currentPrice, product.currency)}
                    </span>
                  )}
                  <span className={`line-through text-white/60 whitespace-nowrap ${compact ? 'text-xs' : 'text-sm'}`}>
                    {formatPrice(originalPrice, product.currency)}
                  </span>
                </div>
              ) : (
                <div className={`font-bold whitespace-nowrap ${compact ? 'text-base' : 'text-xl md:text-2xl'}`}>
                  {formatPrice(currentPrice, product.currency)}
                </div>
              )}
            </div>

            {/* Product-type tag — secondary; truncates if the price needs the room. */}
            <div className={`bg-white/20 backdrop-blur-sm rounded-full whitespace-nowrap min-w-0 truncate ${
              compact ? 'text-[0.6rem] px-2 py-0.5' : 'text-xs px-2.5 py-1'
            }`}>
              {getProductTypeLabel(product.productType)}
            </div>
          </div>
        </div>
      </div>

      {/* Description - Visible on Hover (Desktop) or Always (Mobile if not compact) */}
      {!compact && product.shortDescription && (
        <div className="p-3 bg-white">
          <p className="text-xs text-gray-600 line-clamp-3 leading-snug">
            {product.shortDescription}
          </p>
        </div>
      )}

      {/* Hover Effect Indicator */}
      <div className={`absolute inset-0 border-2 ${theme.accentBorder} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
    </div>
  );
};

export default ProductCard;
